// src/components/admin/AdminNotifications.tsx
// Sistema Avançado de Notificações - IDENTIDADE GRAIA

import React, { useState, useEffect, useCallback } from 'react';
import {
    Send,
    Search,
    X,
    Filter,
    Users,
    CheckSquare,
    Square,
    ChevronDown,
    AlertCircle
} from 'lucide-react';
import './AdminNotifications.css';

interface User {
    _id: string;
    username: string;
    email: string;
    university: string | null;
    course: string | null;
    semester: string | null;
    goal: string | null;
    authProvider: string;
    createdAt: string;
}

interface Filters {
    universities: string[];
    courses: string[];
    semesters: string[];
    goals: string[];
    authProviders: string[];
}

interface AdminNotificationsProps {
    onSendNotification: (title: string, message: string, userIds: string[] | null) => void;
}

const API_BASE_URL = '/api';

const AdminNotifications: React.FC<AdminNotificationsProps> = ({ onSendNotification }) => {
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);

    const [users, setUsers] = useState<User[]>([]);
    const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
    const [filters, setFilters] = useState<Filters>({
        universities: [],
        courses: [],
        semesters: [],
        goals: [],
        authProviders: []
    });

    const [targetMode, setTargetMode] = useState<'all' | 'filter' | 'manual'>('all');
    const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
    const [showFilters, setShowFilters] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUniversity, setSelectedUniversity] = useState('');
    const [selectedCourse, setSelectedCourse] = useState('');
    const [selectedSemester, setSelectedSemester] = useState('');
    const [selectedAuthProvider, setSelectedAuthProvider] = useState('');
    const [dateRange, setDateRange] = useState<'all' | 'week' | 'month' | 'custom'>('all');

    const loadUsers = useCallback(async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/admin/users`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setUsers(data.users);
                setFilters(data.filters);
                setFilteredUsers(data.users);
            }
        } catch (error) {
            console.error('Erro ao carregar usuários:', error);
        }
    }, []);

    const applyFilters = useCallback(() => {
        let result = [...users];

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter(
                u =>
                    u.username.toLowerCase().includes(term) ||
                    u.email.toLowerCase().includes(term)
            );
        }

        if (selectedUniversity) {
            if (selectedUniversity === 'null') {
                result = result.filter(u => !u.university);
            } else {
                result = result.filter(u => u.university === selectedUniversity);
            }
        }

        if (selectedCourse) {
            if (selectedCourse === 'null') {
                result = result.filter(u => !u.course);
            } else {
                result = result.filter(u => u.course === selectedCourse);
            }
        }

        if (selectedSemester) {
            if (selectedSemester === 'null') {
                result = result.filter(u => !u.semester);
            } else {
                result = result.filter(u => u.semester === selectedSemester);
            }
        }

        if (selectedAuthProvider) {
            result = result.filter(u => u.authProvider === selectedAuthProvider);
        }

        if (dateRange !== 'all') {
            const now = new Date();
            const cutoffDate = new Date();

            if (dateRange === 'week') {
                cutoffDate.setDate(now.getDate() - 7);
            } else if (dateRange === 'month') {
                cutoffDate.setMonth(now.getMonth() - 1);
            }

            result = result.filter(u => new Date(u.createdAt) >= cutoffDate);
        }

        setFilteredUsers(result);
    }, [users, searchTerm, selectedUniversity, selectedCourse, selectedSemester, selectedAuthProvider, dateRange]);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    useEffect(() => {
        applyFilters();
    }, [applyFilters]);

    const clearFilters = () => {
        setSearchTerm('');
        setSelectedUniversity('');
        setSelectedCourse('');
        setSelectedSemester('');
        setSelectedAuthProvider('');
        setDateRange('all');
    };

    const hasActiveFilters =
        searchTerm ||
        selectedUniversity ||
        selectedCourse ||
        selectedSemester ||
        selectedAuthProvider ||
        dateRange !== 'all';

    const toggleUserSelection = (userId: string) => {
        const newSelection = new Set(selectedUserIds);
        if (newSelection.has(userId)) {
            newSelection.delete(userId);
        } else {
            newSelection.add(userId);
        }
        setSelectedUserIds(newSelection);
    };

    const selectAllFiltered = () => {
        const newSelection = new Set(filteredUsers.map(u => u._id));
        setSelectedUserIds(newSelection);
    };

    const deselectAll = () => {
        setSelectedUserIds(new Set());
    };

    const handleSubmit = async () => {
        if (!title.trim() || !message.trim()) {
            return;
        }

        let userIdsToSend: string[] | null = null;

        if (targetMode === 'all') {
            userIdsToSend = null;
        } else if (targetMode === 'filter') {
            userIdsToSend = filteredUsers.map(u => u._id);
        } else if (targetMode === 'manual') {
            if (selectedUserIds.size === 0) {
                alert('Selecione pelo menos um usuário');
                return;
            }
            userIdsToSend = Array.from(selectedUserIds);
        }

        setSending(true);
        await onSendNotification(title, message, userIdsToSend);

        setTitle('');
        setMessage('');
        setTargetMode('all');
        setSelectedUserIds(new Set());
        clearFilters();
        setSending(false);
    };

    const getRecipientCount = () => {
        if (targetMode === 'all') return users.length;
        if (targetMode === 'filter') return filteredUsers.length;
        if (targetMode === 'manual') return selectedUserIds.size;
        return 0;
    };

    return (
        <div className="adm-ntf-container adm-page">
            <div className="adm-page-header">
                <h1 className="adm-page-title">Sistema de Notificações</h1>
                <p className="adm-page-subtitle">
                    Envie mensagens para grupos específicos ou usuários selecionados
                </p>
            </div>

            <div className="adm-ntf-form">
                <div className="adm-ntf-form-group">
                    <label>Título da Notificação</label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Ex: Novo deck disponível"
                        maxLength={100}
                    />
                    <span className="adm-ntf-char-count">{title.length}/100</span>
                </div>

                <div className="adm-ntf-form-group">
                    <label>Mensagem</label>
                    <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Digite a mensagem da notificação"
                        rows={5}
                        maxLength={500}
                    />
                    <span className="adm-ntf-char-count">{message.length}/500</span>
                </div>

                <div className="adm-ntf-form-group">
                    <label>Destinatários</label>
                    <div className="adm-ntf-target-mode">
                        <button
                            type="button"
                            className={`adm-ntf-mode-btn ${targetMode === 'all' ? 'active' : ''}`}
                            onClick={() => {
                                setTargetMode('all');
                                setSelectedUserIds(new Set());
                            }}
                        >
                            <Users size={16} />
                            Todos ({users.length})
                        </button>
                        <button
                            type="button"
                            className={`adm-ntf-mode-btn ${targetMode === 'filter' ? 'active' : ''}`}
                            onClick={() => {
                                setTargetMode('filter');
                                setSelectedUserIds(new Set());
                            }}
                        >
                            <Filter size={16} />
                            Grupo Filtrado ({filteredUsers.length})
                        </button>
                        <button
                            type="button"
                            className={`adm-ntf-mode-btn ${targetMode === 'manual' ? 'active' : ''}`}
                            onClick={() => setTargetMode('manual')}
                        >
                            <CheckSquare size={16} />
                            Seleção Manual ({selectedUserIds.size})
                        </button>
                    </div>
                </div>

                {(targetMode === 'filter' || targetMode === 'manual') && (
                    <div className="adm-ntf-filters-section">
                        <div className="adm-ntf-filters-header">
                            <button
                                type="button"
                                className="adm-ntf-btn-toggle-filters"
                                onClick={() => setShowFilters(!showFilters)}
                            >
                                <Filter size={16} />
                                Filtros Avançados
                                <ChevronDown
                                    size={16}
                                    className={showFilters ? 'rotated' : ''}
                                />
                            </button>
                            {hasActiveFilters && (
                                <button
                                    type="button"
                                    className="adm-ntf-btn-clear-filters"
                                    onClick={clearFilters}
                                >
                                    Limpar Filtros
                                </button>
                            )}
                        </div>

                        {showFilters && (
                            <div className="adm-ntf-filters-grid">
                                <div className="adm-ntf-filter-item full-width">
                                    <label>Buscar por Nome ou Email</label>
                                    <div className="adm-ntf-search-wrapper">
                                        <Search size={16} className="adm-ntf-search-icon" />
                                        <input
                                            type="text"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            placeholder="Digite para buscar..."
                                        />
                                        {searchTerm && (
                                            <button
                                                type="button"
                                                className="adm-ntf-clear-search"
                                                onClick={() => setSearchTerm('')}
                                            >
                                                <X size={14} />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="adm-ntf-filter-item">
                                    <label>Universidade</label>
                                    <select
                                        value={selectedUniversity}
                                        onChange={(e) => setSelectedUniversity(e.target.value)}
                                    >
                                        <option value="">Todas</option>
                                        <option value="null">Não declarado</option>
                                        {filters.universities.map(uni => (
                                            <option key={uni} value={uni}>{uni}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="adm-ntf-filter-item">
                                    <label>Curso</label>
                                    <select
                                        value={selectedCourse}
                                        onChange={(e) => setSelectedCourse(e.target.value)}
                                    >
                                        <option value="">Todos</option>
                                        <option value="null">Não declarado</option>
                                        {filters.courses.map(course => (
                                            <option key={course} value={course}>{course}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="adm-ntf-filter-item">
                                    <label>Semestre</label>
                                    <select
                                        value={selectedSemester}
                                        onChange={(e) => setSelectedSemester(e.target.value)}
                                    >
                                        <option value="">Todos</option>
                                        <option value="null">Não declarado</option>
                                        {filters.semesters.map(sem => (
                                            <option key={sem} value={sem}>{sem}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="adm-ntf-filter-item">
                                    <label>Método de Login</label>
                                    <select
                                        value={selectedAuthProvider}
                                        onChange={(e) => setSelectedAuthProvider(e.target.value)}
                                    >
                                        <option value="">Todos</option>
                                        {filters.authProviders.map(provider => (
                                            <option key={provider} value={provider}>
                                                {provider === 'local' ? 'Email' : provider === 'google' ? 'Google' : provider}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="adm-ntf-filter-item">
                                    <label>Data de Cadastro</label>
                                    <select
                                        value={dateRange}
                                        onChange={(e) => setDateRange(e.target.value as any)}
                                    >
                                        <option value="all">Todos</option>
                                        <option value="week">Última semana</option>
                                        <option value="month">Último mês</option>
                                    </select>
                                </div>
                            </div>
                        )}

                        {targetMode === 'manual' && (
                            <div className="adm-ntf-user-selection">
                                <div className="adm-ntf-selection-header">
                                    <span className="adm-ntf-selection-count">
                                        {selectedUserIds.size} de {filteredUsers.length} selecionado(s)
                                    </span>
                                    <div className="adm-ntf-selection-actions">
                                        <button
                                            type="button"
                                            className="adm-ntf-btn-select"
                                            onClick={selectAllFiltered}
                                        >
                                            Selecionar Todos
                                        </button>
                                        {selectedUserIds.size > 0 && (
                                            <button
                                                type="button"
                                                className="adm-ntf-btn-select"
                                                onClick={deselectAll}
                                            >
                                                Desmarcar Todos
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="adm-ntf-users-grid">
                                    {filteredUsers.length === 0 ? (
                                        <div className="adm-ntf-no-users">
                                            <AlertCircle size={24} />
                                            <p>Nenhum usuário encontrado</p>
                                        </div>
                                    ) : (
                                        filteredUsers.map(user => (
                                            <div
                                                key={user._id}
                                                className={`adm-ntf-user-card ${selectedUserIds.has(user._id) ? 'selected' : ''}`}
                                                onClick={() => toggleUserSelection(user._id)}
                                            >
                                                <div className="adm-ntf-user-checkbox">
                                                    {selectedUserIds.has(user._id) ? (
                                                        <CheckSquare size={18} />
                                                    ) : (
                                                        <Square size={18} />
                                                    )}
                                                </div>
                                                <div className="adm-ntf-user-avatar">
                                                    {user.username.charAt(0).toUpperCase()}
                                                </div>
                                                <div className="adm-ntf-user-info">
                                                    <div className="adm-ntf-user-name">{user.username}</div>
                                                    <div className="adm-ntf-user-email">{user.email}</div>
                                                    {user.university && (
                                                        <div className="adm-ntf-user-meta">{user.university}</div>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="adm-ntf-summary">
                    <div className="adm-ntf-summary-info">
                        <AlertCircle size={16} />
                        <span>
                            Esta notificação será enviada para{' '}
                            <strong>{getRecipientCount()}</strong>{' '}
                            {getRecipientCount() === 1 ? 'usuário' : 'usuários'}
                        </span>
                    </div>
                </div>

                <button
                    type="button"
                    className="adm-ntf-btn-send"
                    onClick={handleSubmit}
                    disabled={sending || !title.trim() || !message.trim() || getRecipientCount() === 0}
                >
                    <Send size={16} />
                    {sending ? 'Enviando...' : 'Enviar Notificação'}
                </button>
            </div>
        </div>
    );
};

export default AdminNotifications;